import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { fmtNOK } from "@/lib/format";
import { BLANK, DRAFT_KEY, EMPTY, STEPS, blocker } from "@/lib/veiviser";
import { useVeiviser } from "@/hooks/useVeiviser";
import { Footer, Progress, StepList } from "@/components/veiviser/WizardChrome";
import { StepKunde } from "@/components/veiviser/StepKunde";
import { StepMal } from "@/components/veiviser/StepMal";
import { StepOmfang } from "@/components/veiviser/StepOmfang";
import { StepPeriode } from "@/components/veiviser/StepPeriode";
import { StepOppsummering } from "@/components/veiviser/StepOppsummering";
import { LiveSummary } from "@/components/veiviser/LiveSummary";

const EASE = [0.22, 1, 0.36, 1];
const STEP_KEY = "los.veiviser.step";
const readStep = () => { try { const s = JSON.parse(sessionStorage.getItem(STEP_KEY) || "null"); const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null"); return d?.customer?.name && s && s.at >= 0 && s.at < STEPS.length ? { at: s.at, reached: Math.max(s.at, s.reached || 0) } : { at: 0, reached: 0 }; } catch { return { at: 0, reached: 0 }; } };

/**
 * /ny/veiviser – «Nytt tilbud» the guided way: customer and vessel, the kind of job, the posts that apply, period and
 * deadlines – and the case opens in Kalkyle with LOS's own standard posts, priced for this hull. The other way in
 * (drop the customer's documents in the conversation) stays one click away in the header.
 */
export default function VeiviserPage() {
  const navigate = useNavigate();
  const w = useVeiviser();
  const { v } = w;
  const [at, setAt] = useState(() => readStep().at);
  const [reached, setReached] = useState(() => readStep().reached);
  const [tried, setTried] = useState(false);
  const [creating, setCreating] = useState(false);
  const scroller = useRef(null);
  const step = STEPS[at].key;
  const block = blocker(step, v);
  const dirty = JSON.stringify(v) !== JSON.stringify(EMPTY);

  useEffect(() => { scroller.current?.scrollTo({ top: 0 }); setTried(false); sessionStorage.setItem(STEP_KEY, JSON.stringify({ at, reached })); }, [at, reached]);

  const go = (i) => { setAt(i); setReached((r) => Math.max(r, i)); };
  const create = async () => {
    setCreating(true);
    try {
      const p = await w.create();
      sessionStorage.removeItem(STEP_KEY);
      toast.success(`${p.name} er opprettet som sak ${p.number}`);
      navigate(`/p/${p.id}?tab=${v.template && v.template !== BLANK ? "kalkyle" : "krav"}`);
    } catch (e) {
      toast.error(errMsg(e));
      setCreating(false);
    }
  };
  const next = () => {
    if (block) { setTried(true); return; }
    if (at < STEPS.length - 1) go(at + 1);
    else create();
  };
  const onKey = (e) => {
    if (e.key !== "Enter" || e.defaultPrevented || e.shiftKey || (e.target?.tagName || "").toLowerCase() === "textarea") return;
    if ((e.target?.tagName || "").toLowerCase() === "button") return;
    e.preventDefault();
    next();
  };
  const restart = () => { w.reset(); setAt(0); setReached(0); };

  const body = step === "kunde" ? <StepKunde v={v} set={w.set} setV={w.setV} />
    : step === "mal" ? <StepMal v={v} setV={w.setV} catalog={w.catalog} />
      : step === "omfang" ? <StepOmfang v={v} setV={w.setV} preview={w.preview} previewing={w.previewing} />
        : step === "periode" ? <StepPeriode v={v} set={w.set} catalog={w.catalog} preview={w.preview} template={w.template} cap={w.cap} />
          : <StepOppsummering v={v} set={w.set} template={w.template} totals={w.totals} onGo={go} />;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-white" data-testid="veiviser-page" data-step={step}>
      <Progress at={at} />
      <header className="flex h-14 shrink-0 items-center justify-between gap-3 px-5 sm:px-8" data-testid="vv-header">
        <div className="flex min-w-0 items-baseline gap-2.5">
          <h1 className="truncate text-[15px] font-semibold tracking-[-0.01em]" data-testid="vv-title">Nytt tilbud</h1>
          <span className="hidden text-[12.5px] text-muted-foreground sm:inline">Veiviser · lagres mens du jobber</span>
        </div>
        <div className="flex items-center gap-1">
          {dirty ? <button type="button" onClick={restart} className="hidden h-9 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground sm:inline-flex" data-testid="vv-restart"><RotateCcw className="h-3.5 w-3.5" /> Start på nytt</button> : null}
          <Link to="/ny" className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13px] font-medium text-foreground/75 transition-colors hover:bg-black/[0.05] hover:text-foreground" data-testid="vv-to-upload"><Upload className="h-3.5 w-3.5" /><span className="hidden sm:inline">Har du kundens dokumenter? Last dem opp</span><span className="sm:hidden">Last opp</span></Link>
          <button type="button" onClick={() => navigate("/ny")} className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground" aria-label="Lukk veiviseren" data-testid="vv-close"><X className="h-4 w-4" /></button>
        </div>
      </header>
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto" onKeyDown={onKey}>
        <div className="mx-auto grid w-full max-w-[1180px] gap-10 px-5 pb-16 pt-4 sm:px-8 lg:grid-cols-[190px_minmax(0,1fr)_270px]">
          <div className="hidden lg:block"><div className="sticky top-6"><StepList at={at} reached={reached} onGo={go} /></div></div>
          <main className="min-w-0">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.26, ease: EASE }} data-testid={`vv-panel-${step}`}>
                <div className="text-[12.5px] font-medium text-primary" data-testid="vv-step-label">Steg {at + 1} av {STEPS.length}<span className="text-muted-foreground lg:hidden"> · {STEPS[at].label}</span></div>
                <h2 className="mt-2 font-display text-[28px] font-bold leading-[1.1] tracking-[-0.03em] text-[hsl(var(--navy))] sm:text-[34px]" data-testid="vv-step-title">{STEPS[at].lead}</h2>
                {tried && block ? <p className="mt-3 text-[13.5px] font-medium text-[hsl(var(--v-blocked))]" role="alert" data-testid="vv-error">{block}</p> : null}
                {w.error ? <p className="mt-3 text-[13px] text-[hsl(var(--v-blocked))]" data-testid="vv-load-error">{w.error}</p> : null}
                <div className="mt-8">{body}</div>
              </motion.div>
            </AnimatePresence>
          </main>
          <div className="hidden lg:block"><LiveSummary v={v} template={w.template} totals={w.totals} previewing={w.previewing} /></div>
        </div>
      </div>
      <Footer at={at} onBack={() => go(Math.max(0, at - 1))} onNext={next} busy={creating}
        nextLabel={at === STEPS.length - 1 ? (creating ? "Oppretter …" : "Opprett tilbudsutkast") : "Neste"}
        summary={w.totals.posts ? <span className="text-[12.5px] text-muted-foreground"><span className="font-semibold text-foreground">{w.totals.posts} poster</span>{w.totals.sum ? ` · ${fmtNOK(w.totals.sum)} kr` : ""}</span> : null} />
    </div>
  );
}
